import { useState } from 'react'
import { fetchHostPubkey, putSecret } from '../../api/ws'
import {
  base64Decode,
  base64Encode,
  encryptToHostPubkey,
} from '../../lib/secretEnvelope'

// Shared "set secret value" form. Used by SecretsSheet (add / rotate).
//
// All crypto happens here: fetches the host's X25519 pubkey, encrypts
// the plaintext value to it (X25519 + HKDF-SHA256 + ChaCha20-Poly1305
// matching secrets_crypto.go on the daemon and the server), then
// uploads the base64-encoded envelope via secrets_put.
//
// Plaintext never leaves the browser unencrypted.
export function SecretValueForm({
  hostID,
  hostLabel,
  nameLocked,
  initialName,
  submitLabel,
  onDone,
  onCancel,
}: {
  hostID: string
  hostLabel?: string
  nameLocked?: boolean
  initialName?: string
  submitLabel?: string
  onDone: (id?: string) => void
  onCancel?: () => void
}) {
  const [name, setName] = useState(initialName ?? '')
  const [value, setValue] = useState('')
  const [purpose, setPurpose] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [reveal, setReveal] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    const trimmedName = name.trim()
    if (!trimmedName) {
      setError('Name is required.')
      return
    }
    if (!value) {
      setError('Value is required.')
      return
    }
    setBusy(true)
    try {
      const pubResp = await fetchHostPubkey(hostID)
      if (pubResp.error) {
        setError(pubResp.error)
        return
      }
      if (!pubResp.pubkey) {
        setError(
          'This host has not enrolled an encryption key yet. ' +
            'Run any `hearth secret` command on the host once to enroll, then retry.',
        )
        return
      }
      const rawPub = base64Decode(pubResp.pubkey)
      const plaintext = new TextEncoder().encode(value)
      const envelope = encryptToHostPubkey(rawPub, plaintext)
      const ciphertextB64 = base64Encode(envelope)

      const putResp = await putSecret({
        hostID,
        name: trimmedName,
        ciphertextB64,
        purpose: purpose.trim() || undefined,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
      })
      if (putResp.error) {
        setError(putResp.error)
        return
      }
      onDone(putResp.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form
      onSubmit={submit}
      style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
    >
      {hostLabel && (
        <div style={{ fontSize: 12, opacity: 0.65 }}>
          Host: <code style={{ fontFamily: 'monospace' }}>{hostLabel}</code>
        </div>
      )}
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 12, opacity: 0.7 }}>Name</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={nameLocked}
          placeholder="e.g. HA_TOKEN"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          style={{ fontSize: 16, fontFamily: 'monospace' }}
        />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 12, opacity: 0.7 }}>Value</span>
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            type={reveal ? 'text' : 'password'}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            style={{ fontSize: 16, fontFamily: 'monospace', flex: 1 }}
          />
          <button
            type="button"
            onClick={() => setReveal((r) => !r)}
            style={{ fontSize: 12 }}
          >
            {reveal ? 'Hide' : 'Show'}
          </button>
        </div>
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 12, opacity: 0.7 }}>Purpose (optional)</span>
        <input
          value={purpose}
          onChange={(e) => setPurpose(e.target.value)}
          placeholder='e.g. "HA long-lived token"'
          style={{ fontSize: 16 }}
        />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 12, opacity: 0.7 }}>
          Expires (optional)
        </span>
        <input
          type="datetime-local"
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
          style={{ fontSize: 16 }}
        />
      </label>
      {error && (
        <div
          style={{
            padding: '8px 12px',
            background: '#fee',
            color: '#900',
            borderRadius: 4,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button
          type="submit"
          disabled={busy || !name.trim() || !value}
          style={{ padding: '8px 12px' }}
        >
          {busy ? 'Encrypting…' : (submitLabel ?? 'Save')}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        )}
      </div>
    </form>
  )
}
